import { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Eye, Star, MessageSquare, Flag, TrendingUp, ShoppingCart, CornerDownRight, Send, Coins, Wallet, X, Lock, Heart, ExternalLink, ChevronRight, Settings2, UserPlus, Users, ImageOff } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UnlockGateModal from "@/components/UnlockGateModal";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { toast } from "sonner";

const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k" : `${n ?? 0}`);
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Lightweight inline SVG sparkline — no chart library needed.
const Sparkline = ({ values = [], stroke = "#4f7746", fill = "rgba(79,119,70,0.12)", w = 260, h = 44 }) => {
  if (!values.length) return null;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const pts = values.map((v, i) => [i * step, h - (v / max) * (h - 4) - 2]);
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
      <path d={area} fill={fill} />
      <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const StatTile = ({ Icon, label, value, sub, testid, accent = "#4f7746" }) => (
  <div data-testid={testid} className="rounded-2xl border-2 border-[#5d8a52] bg-white px-1.5 py-2.5 flex flex-col items-center text-center gap-1">
    <div className="flex flex-col items-center gap-1 min-h-[46px]">
      <Icon size={20} style={{ color: accent }} className="shrink-0" />
      <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">{label}</span>
    </div>
    <div className="text-2xl font-extrabold leading-none text-[#171426]">{value}</div>
    {sub && <div className="text-[11px] font-semibold text-black">{sub}</div>}
  </div>
);

export default function StudioDashboard({ stats, onOpen, onChanged, trendsOpen = false, onCloseTrends }) {
  const { user } = useAuth();
  const ctx = useOutletContext() || {};
  const navigate = useNavigate();
  const [trendRange, setTrendRange] = useState(7);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [gate, setGate] = useState(null); // { feature, min, count }
  const [panel, setPanel] = useState(null); // 'posts' | 'balance' | 'tips' | 'love' | 'amazon' | null
  const [buyOpen, setBuyOpen] = useState(false);
  const [amazonExpand, setAmazonExpand] = useState(null); // manual_id whose item list is expanded
  const [following, setFollowing] = useState(null);

  const loadFollowing = () => api.get("/me/following").then(({ data }) => setFollowing(data)).catch(() => setFollowing({ following: [], count: 0 }));
  useEffect(() => { loadFollowing(); }, []);
  const followCount = following?.count ?? 0;

  const LOW_BALANCE = 399; // cents ($3.99 — one video's worth)
  const credits = Number(user?.credits ?? 0);
  const cash = (credits / 100).toFixed(2);
  const unlimited = !!user?.unlimited;
  const lowBalance = !unlimited && credits < LOW_BALANCE;

  const unlocks = stats?.unlocks || {};
  const pub = unlocks.published_count ?? 0;
  const tipsUnlocked = unlocks.tips_unlocked ?? true;
  const amazonUnlocked = unlocks.amazon_unlocked ?? true;

  const togglePanel = (name) => { setReplyOpen(null); setPanel((p) => (p === name ? null : name)); };

  const openLove = () => togglePanel("love");

  const openAmazon = () => {
    if (!amazonUnlocked) { setGate({ feature: "Amazon Affiliate ID", min: unlocks.amazon_min || 30, count: pub }); return; }
    togglePanel("amazon");
  };

  if (!stats) return null;
  const t = stats.totals || {};
  const best = [...(stats.videos || [])].sort((a, b) => b.views - a.views)[0];
  const hasData = (t.videos || 0) > 0;
  const trend = stats.trend || { days: [], views: [], clicks: [] };
  const tViews = (trend.views || []).slice(-trendRange);
  const tClicks = (trend.clicks || []).slice(-trendRange);
  const trendViewsSum = tViews.reduce((a, b) => a + b, 0);
  const trendClicksSum = tClicks.reduce((a, b) => a + b, 0);
  const trendLabel = trendRange === 1 ? "24h" : `${trendRange}d`;

  const sendReply = async (c) => {
    if (!replyText.trim()) return;
    setReplyBusy(true);
    try {
      await api.post(`/community/${c.manual_id}/comments/${c.id}/reply`, { text: replyText.trim() });
      toast.success("Reply posted");
      setReplyOpen(null);
      setReplyText("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post reply");
    } finally {
      setReplyBusy(false);
    }
  };

  return (
    <div className="mb-6" data-testid="studio-dashboard">
      <div className="grid grid-cols-4 lg:grid-cols-8 gap-2" data-testid="studio-kpis">
        <button
          data-testid="kpi-love"
          onClick={openLove}
          className="order-7 rounded-2xl border-2 border-[#5d8a52] bg-white px-1.5 py-2.5 flex flex-col items-center text-center gap-1 transition-transform active:scale-95 hover:shadow-md"
          title="See who loved your videos"
        >
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <Heart size={20} className="shrink-0 text-[#e11d48]" fill="#e11d48" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Love</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{fmt(t.loves || 0)}</div>
          <div className="text-[11px] font-semibold text-black">tap to view</div>
        </button>
        <button
          data-testid="kpi-views"
          onClick={() => togglePanel("views")}
          className="order-2 rounded-2xl bg-white border-2 border-[#5d8a52] px-1.5 py-2.5 flex flex-col items-center text-center gap-1 hover:shadow-sm transition-all active:scale-[.98]"
        >
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <Eye size={20} className="shrink-0 text-[#16a34a]" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Views</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{fmt(t.views)}</div>
          <div className="text-[11px] font-semibold text-black">{t.videos} video{t.videos === 1 ? "" : "s"} · tap</div>
        </button>
        <button
          data-testid="kpi-clicks"
          onClick={openAmazon}
          className="order-3 relative rounded-2xl border-2 border-[#5d8a52] bg-white px-1.5 py-2.5 flex flex-col items-center text-center gap-1 transition-transform active:scale-95 hover:shadow-md"
          title={amazonUnlocked ? "Set up your Amazon Associate ID" : "Locked — publish more videos to unlock"}
        >
          {!amazonUnlocked && <span className="absolute top-1.5 right-1.5 text-[#c2410c]"><Lock size={13} /></span>}
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <ShoppingCart size={20} style={{ color: "#c2410c" }} className="shrink-0" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Amazon clicks</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{fmt(t.clicks)}</div>
          <div className="text-[11px] font-semibold text-black">{amazonUnlocked ? `${t.ctr}% CTR · tap` : "tap to unlock"}</div>
        </button>
        <button
          data-testid="kpi-rating"
          onClick={() => togglePanel("ratings")}
          className="order-4 rounded-2xl bg-white border-2 border-[#5d8a52] px-1.5 py-2.5 flex flex-col items-center text-center gap-1 hover:shadow-sm transition-all active:scale-[.98]"
        >
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <Star size={20} className="shrink-0 text-[#ca8a04]" fill="#ca8a04" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Avg rating</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{t.avg_rating ? t.avg_rating.toFixed(1) : "—"}</div>
          <div className="text-[11px] font-semibold text-black">{t.ratings} rating{t.ratings === 1 ? "" : "s"} · tap</div>
        </button>
        <button
          data-testid="kpi-comments"
          onClick={() => togglePanel("posts")}
          className="order-6 rounded-2xl border-2 border-[#5d8a52] bg-white px-1.5 py-2.5 flex flex-col items-center text-center gap-1 transition-transform active:scale-95 hover:shadow-md"
          title="Read & reply to recent posts"
        >
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <MessageSquare size={20} style={{ color: "#2563eb" }} className="shrink-0" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Posts</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{fmt(t.comments)}</div>
          <div className="text-[11px] font-semibold text-black">tap to manage</div>
        </button>
        <button
          data-testid="kpi-following"
          onClick={() => { togglePanel("follow"); loadFollowing(); }}
          title="Creators you follow"
          className="order-5 rounded-2xl border-2 border-[#5d8a52] px-1.5 py-2.5 flex flex-col items-center text-center gap-1 transition-all active:scale-95 hover:shadow-md bg-white"
        >
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <UserPlus size={20} className="shrink-0 text-[#2563eb]" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Follow</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{fmt(followCount)}</div>
          <div className="text-[11px] font-semibold text-black">tap</div>
        </button>
        <button
          data-testid="kpi-balance"
          onClick={() => togglePanel("balance")}
          className="order-1 relative rounded-2xl border-2 border-[#5d8a52] bg-white px-1.5 py-2.5 flex flex-col items-center text-center gap-1 transition-transform active:scale-95 hover:shadow-md"
          title="View your cash balance"
        >
          {lowBalance && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#dc2626]" />}
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <Wallet size={20} style={{ color: "#16a34a" }} className="shrink-0" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Cash balance</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{unlimited ? "∞" : `$${cash}`}</div>
          <div className={`text-[11px] font-semibold ${lowBalance ? "text-[#dc2626]" : "text-black"}`}>{unlimited ? "unlimited" : lowBalance ? "low · add funds" : "USD"}</div>
        </button>
        <button
          data-testid="kpi-flags"
          onClick={() => togglePanel("flags")}
          className="order-8 rounded-2xl border-2 border-[#5d8a52] bg-white px-1.5 py-2.5 flex flex-col items-center text-center gap-1 transition-all active:scale-95 hover:shadow-md"
          title={t.flags ? "See who flagged what" : "All clear"}
        >
          <div className="flex flex-col items-center gap-1 min-h-[46px]">
            <Flag size={20} className="shrink-0 text-[#dc2626]" fill="#dc2626" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-black leading-tight">Flags</span>
          </div>
          <div className="text-2xl font-extrabold leading-none text-[#171426]">{t.flags}</div>
          <div className="text-[11px] font-semibold text-black">{t.flags ? "tap to review" : "all clear · tap"}</div>
        </button>
      </div>

      {/* Comments — toggled open from the Posts card (not persistent, saves room) */}
      {panel === "posts" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-3 sms-fade-up" data-testid="posts-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-sm font-bold text-[#171426]">
              <MessageSquare size={15} className="text-[#2563eb]" /> Comments
              <span className="text-xs font-bold text-[#9a96ac]">· {fmt(t.comments)}</span>
            </div>
            <button data-testid="posts-card-close" onClick={() => { setPanel(null); setReplyOpen(null); }} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>
          {(stats.recent_comments || []).length === 0 ? (
            <div className="py-8 text-center text-[#9a96ac]">
              <MessageSquare size={26} className="mx-auto mb-2 text-[#bcd0f0]" />
              <p className="font-semibold text-[#6b6880]">No comments yet.</p>
              <p className="text-sm mt-1">When viewers post on your videos, they'll show up here.</p>
            </div>
          ) : (
            <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="posts-list">
              {stats.recent_comments.map((c) => (
                <div key={c.id} data-testid={`recent-comment-${c.id}`} className="rounded-xl bg-[#f7faf5] px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <button onClick={() => onOpen?.(c.manual_id)} className="text-xs font-bold text-[#4f7746] truncate hover:underline">{c.emoji} {c.author}</button>
                    <span className="text-[10px] text-[#9a96ac] shrink-0">{timeAgo(c.created_at)}</span>
                  </div>
                  <div className="text-sm text-[#2c2941] leading-snug mt-0.5">{c.text}</div>
                  <button onClick={() => onOpen?.(c.manual_id)} className="text-[10px] text-[#9a96ac] mt-0.5 truncate hover:underline">on “{c.video_title}”</button>

                  {c.reply ? (
                    <div className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-[#eef3ea] px-2.5 py-1.5" data-testid={`comment-reply-${c.id}`}>
                      <CornerDownRight size={13} className="text-[#4f7746] mt-0.5 shrink-0" />
                      <div className="text-xs text-[#2c2941] leading-snug"><span className="font-bold text-[#4f7746]">You: </span>{c.reply.text}</div>
                    </div>
                  ) : replyOpen === c.id ? (
                    <div className="mt-1.5 flex items-center gap-1.5" data-testid={`reply-box-${c.id}`}>
                      <input
                        autoFocus
                        data-testid={`reply-input-${c.id}`}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") sendReply(c); }}
                        maxLength={300}
                        placeholder="Reply as the creator…"
                        className="flex-1 text-sm px-3 py-1.5 rounded-lg border-2 border-[#cfe0c7] focus:border-[#5d8a52] outline-none"
                      />
                      <button data-testid={`reply-send-${c.id}`} onClick={() => sendReply(c)} disabled={replyBusy || !replyText.trim()}
                        className="p-2 rounded-lg bg-[#4f7746] text-white disabled:opacity-50"><Send size={14} /></button>
                    </div>
                  ) : (
                    <button data-testid={`reply-btn-${c.id}`} onClick={() => { setReplyOpen(c.id); setReplyText(""); }}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#4f7746] hover:underline">
                      <CornerDownRight size={12} /> Reply
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cash balance — toggled open from the Cash balance card */}
      {panel === "balance" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-4 sms-fade-up" data-testid="balance-card">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5 text-sm font-bold text-[#171426]">
              <Wallet size={15} className="text-[#0d9488]" /> Cash Balance
            </div>
            <button data-testid="balance-card-close" onClick={() => setPanel(null)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1 text-3xl font-extrabold text-[#171426] tabular-nums" data-testid="balance-amount">
              {unlimited ? "∞" : <><Wallet size={22} className="text-[#0d9488]" /> ${cash}</>}
              <span className="text-sm font-bold text-[#0d9488] ml-1">{unlimited ? "unlimited" : "USD"}</span>
            </div>
            {!unlimited && (
              <button
                data-testid="balance-topup-btn"
                onClick={() => setBuyOpen(true)}
                className={`ml-auto inline-flex items-center justify-center gap-2 font-extrabold py-2.5 px-5 rounded-2xl transition-transform hover:scale-[1.02] ${lowBalance ? "text-white" : "text-[#0d9488] bg-[#e6f5f3] hover:bg-[#d5efec] font-bold"}`}
                style={lowBalance ? { background: "linear-gradient(135deg,#0d9488,#0f766e)" } : undefined}
              >
                <Wallet size={17} /> {lowBalance ? "Add funds" : "Add more funds"}
              </button>
            )}
          </div>
          {unlimited ? (
            <p className="text-[13px] text-[#6b6880] mt-3 leading-snug">Your admin account has an unlimited balance — create as many videos as you like.</p>
          ) : lowBalance ? (
            <div className="mt-3 rounded-xl bg-[#fdecec] border border-[#f4c2c2] px-3 py-2.5 text-[13px] font-semibold text-[#b91c1c]" data-testid="balance-low-warning">
              You're running low — each video costs a little to render. Add funds so you never lose your place mid-creation.
            </div>
          ) : (
            <p className="text-[13px] text-[#6b6880] mt-3 leading-snug">You're all set. Each video is one flat $3.99 — everything included — so keep a comfortable balance and you're never interrupted.</p>
          )}
        </div>
      )}

      {/* Love — toggled open from the Love card */}
      {panel === "love" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-3 sms-fade-up" data-testid="love-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#171426]">
              <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#f43f5e,#e11d48)" }}><Heart size={15} className="text-white" fill="#fff" /></span>
              Love received <span className="text-xs font-bold text-[#be123c]">· {fmt(t.loves || 0)} ❤️</span>
            </div>
            <button data-testid="love-card-close" onClick={() => setPanel(null)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>
          <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="love-list">
            {(stats.recent_loves || []).length === 0 ? (
              <div className="py-8 text-center text-[#9a96ac]">
                <Heart size={26} className="mx-auto mb-2 text-[#f7c6d2]" />
                <p className="font-semibold text-[#6b6880]">No Love yet.</p>
                <p className="text-sm mt-1">When viewers heart your videos, they'll show up here.</p>
              </div>
            ) : (
              stats.recent_loves.map((lv) => (
                <button
                  key={lv.id}
                  data-testid={`love-row-${lv.id}`}
                  onClick={() => { if (lv.manual_id) onOpen?.(lv.manual_id); }}
                  className="w-full text-left rounded-2xl bg-[#fdecef] border border-[#f7c6d2] px-3 py-2.5 flex items-center gap-3 hover:bg-[#fbdae2] transition-colors"
                >
                  <span className="shrink-0 flex items-center gap-0.5 text-sm font-extrabold text-[#171426] bg-white rounded-full px-2 py-1 border border-[#f7c6d2]">
                    <span>{lv.from_avatar?.emoji || "🙂"}</span><span className="tabular-nums">{lv.from_avatar?.tag || ""}</span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-extrabold text-[#be123c]">
                      <Heart size={13} fill="#e11d48" className="text-[#e11d48]" /> Loved your video
                      <span className="text-[10px] font-semibold text-[#a7a3b8] ml-auto">{timeAgo(lv.created_at)}</span>
                    </div>
                    <div className="text-[11px] text-[#9a96ac] truncate mt-0.5">on “{lv.manual_title}”</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* 🚩 Flags — who reported and why, opened inline from the Flags card */}
      {panel === "flags" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-3 sms-fade-up" data-testid="flags-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#171426]">
              <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#f87171,#dc2626)" }}><Flag size={14} className="text-white" fill="#fff" /></span>
              Flags <span className="text-xs font-bold text-[#b91c1c]">· {fmt(t.flags || 0)} report{t.flags === 1 ? "" : "s"}</span>
            </div>
            <button data-testid="flags-card-close" onClick={() => setPanel(null)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>
          <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="flags-list">
            {(stats.recent_flags || []).length === 0 ? (
              <div className="py-8 text-center text-[#9a96ac]">
                <Flag size={26} className="mx-auto mb-2 text-[#f7c3c3]" />
                <p className="font-semibold text-[#6b6880]">No flags — you're all clear! 🎉</p>
                <p className="text-sm mt-1">If a viewer reports a video, it'll show up here with their reason.</p>
              </div>
            ) : (
              stats.recent_flags.map((f, i) => (
                <button
                  key={i}
                  data-testid={`flag-row-${i}`}
                  onClick={() => { if (f.manual_id) onOpen?.(f.manual_id); }}
                  className="w-full text-left rounded-2xl bg-[#fef2f2] border border-[#f6cccc] px-3 py-2.5 flex items-start gap-3 hover:bg-[#fde7e7] transition-colors"
                >
                  <span className="shrink-0 flex items-center gap-0.5 text-sm font-extrabold text-[#171426] bg-white rounded-full px-2 py-1 border border-[#f6cccc] mt-0.5">
                    {f.anonymous
                      ? <Flag size={13} className="text-[#dc2626]" />
                      : <><span>{f.from_avatar?.emoji || "🙂"}</span><span className="tabular-nums">{f.from_avatar?.tag || ""}</span></>}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold text-[#171426]">{f.anonymous ? "A viewer" : f.who}</span>
                      <span className="text-[10px] font-extrabold uppercase tracking-wide text-white bg-[#dc2626] rounded-full px-1.5 py-0.5" data-testid={`flag-reason-${i}`}>{f.reason}</span>
                      <span className="text-[10px] font-semibold text-[#c69a9a] ml-auto">{timeAgo(f.created_at)}</span>
                    </div>
                    {f.details ? (
                      <div className="text-[12px] text-[#7a4a4a] mt-1 leading-snug">“{f.details}”</div>
                    ) : (
                      <div className="text-[12px] text-[#b79a9a] italic mt-1">No note left.</div>
                    )}
                    <div className="text-[11px] text-[#b08a8a] truncate mt-0.5">on “{(f.manual_title || "your video").slice(0, 28)}{(f.manual_title || "").length > 28 ? "…" : ""}”</div>
                  </div>
                  {f.hero_image_id && (
                    <img src={imageUrl(f.hero_image_id)} alt="" className="shrink-0 w-12 h-12 rounded-lg object-cover border border-[#f6cccc]" data-testid={`flag-thumb-${i}`} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* 👥 Following — creators you follow, opened inline from the Follow card */}
      {panel === "follow" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-3 sms-fade-up" data-testid="follow-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#171426]">
              <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#7c5cff,#5b21b6)" }}><UserPlus size={15} className="text-white" /></span>
              Following <span className="text-xs font-bold text-[#6d4bff]">· {followCount}</span>
            </div>
            <button data-testid="follow-card-close" onClick={() => setPanel(null)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>
          <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="follow-list">
            {!following ? null : followCount === 0 ? (
              <div className="py-8 text-center text-[#9a96ac]">
                <Users size={26} className="mx-auto mb-2 text-[#cbb9ff]" />
                <p className="font-semibold text-[#6b6880]">You're not following anyone yet.</p>
                <p className="text-sm mt-1">Open a creator's page and tap <b>Follow</b> to keep up with their newest videos.</p>
                <button onClick={() => navigate("/")} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white px-4 py-2 rounded-full" style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>
                  Explore creators <ChevronRight size={15} />
                </button>
              </div>
            ) : (
              following.following.map((c) => (
                <button
                  key={c.username}
                  data-testid={`following-row-${c.username}`}
                  onClick={() => navigate(`/creator/${c.username}`)}
                  className="w-full text-left rounded-2xl bg-[#f7f6fb] border border-[#e6e2f5] px-3 py-2.5 flex items-center gap-3 hover:bg-[#f0edfb] transition-colors"
                >
                  <span className="shrink-0 w-11 h-11 rounded-2xl bg-white border border-[#e6e2f5] flex items-center justify-center">
                    {c.avatar ? (
                      <span className="flex items-baseline gap-0.5"><span className="text-xl leading-none">{c.avatar.emoji}</span><span className="text-xs font-extrabold text-[#6d4bff]">{c.avatar.tag}</span></span>
                    ) : <ImageOff size={18} className="text-[#c7c2dd]" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[#171426] truncate">{c.display_name}</div>
                    <div className="text-xs text-[#9a96ac]">@{c.username} · {c.videos} video{c.videos === 1 ? "" : "s"}</div>
                  </div>
                  <ChevronRight size={16} className="text-[#b7b3c8] shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* 👁 Views leaderboard — top videos by views, opened from the Views card */}
      {panel === "views" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-3 sms-fade-up" data-testid="views-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#171426]">
              <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#60a5fa,#2563eb)" }}><Eye size={15} className="text-white" /></span>
              Most-watched <span className="text-xs font-bold text-[#1d4ed8]">· {fmt(t.views)} total views</span>
            </div>
            <button data-testid="views-card-close" onClick={() => setPanel(null)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>
          <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="views-list">
            {(() => {
              const top = [...(stats.videos || [])].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8);
              if (top.length === 0 || top.every((v) => !v.views)) {
                return (
                  <div className="py-8 text-center text-[#9a96ac]">
                    <Eye size={26} className="mx-auto mb-2 text-[#bcd4f5]" />
                    <p className="font-semibold text-[#6b6880]">No views yet.</p>
                    <p className="text-sm mt-1">Once people watch your videos, your top performers rank here.</p>
                  </div>
                );
              }
              const maxV = Math.max(...top.map((v) => v.views || 0), 1);
              return top.map((v, i) => (
                <button
                  key={v.id}
                  data-testid={`views-row-${i}`}
                  onClick={() => { if (v.id) onOpen?.(v.id); }}
                  className="w-full text-left rounded-2xl bg-[#eef4ff] border border-[#cfe0fb] px-3 py-2.5 flex items-center gap-3 hover:bg-[#e2edff] transition-colors"
                >
                  <span className="shrink-0 w-6 h-6 rounded-full bg-white border border-[#cfe0fb] flex items-center justify-center text-xs font-extrabold text-[#1d4ed8] tabular-nums">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-[#171426] truncate">{(v.title || "your video").slice(0, 30)}{(v.title || "").length > 30 ? "…" : ""}</div>
                    <div className="mt-1 h-1.5 rounded-full bg-[#d7e4fb] overflow-hidden">
                      <div className="h-full rounded-full bg-[#2563eb]" style={{ width: `${Math.round(100 * (v.views || 0) / maxV)}%` }} />
                    </div>
                  </div>
                  <span className="shrink-0 flex items-center gap-1 text-sm font-extrabold text-[#1d4ed8] tabular-nums"><Eye size={13} /> {fmt(v.views || 0)}</span>
                  {v.hero_image_id && (
                    <img src={imageUrl(v.hero_image_id)} alt="" className="shrink-0 w-12 h-12 rounded-lg object-cover border border-[#cfe0fb]" data-testid={`views-thumb-${i}`} />
                  )}
                </button>
              ));
            })()}
          </div>
        </div>
      )}

      {/* ⭐ Ratings received — who sent how many stars, on which video */}
      {panel === "ratings" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-3 sms-fade-up" data-testid="ratings-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#171426]">
              <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#fbbf24,#ca8a04)" }}><Star size={15} className="text-white" fill="#fff" /></span>
              Ratings received <span className="text-xs font-bold text-[#a16207]">· {t.avg_rating ? t.avg_rating.toFixed(1) : "—"} avg · {fmt(t.ratings || 0)} total</span>
            </div>
            <button data-testid="ratings-card-close" onClick={() => setPanel(null)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>
          <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="ratings-list">
            {(stats.recent_ratings || []).length === 0 ? (
              <div className="py-8 text-center text-[#9a96ac]">
                <Star size={26} className="mx-auto mb-2 text-[#fce6a8]" />
                <p className="font-semibold text-[#6b6880]">No ratings yet.</p>
                <p className="text-sm mt-1">When viewers rate your how-to's, each star they send shows up here.</p>
              </div>
            ) : (
              stats.recent_ratings.map((rt, i) => (
                <button
                  key={i}
                  data-testid={`rating-row-${i}`}
                  onClick={() => { if (rt.manual_id) onOpen?.(rt.manual_id); }}
                  className="w-full text-left rounded-2xl bg-[#fffbeb] border border-[#f5e2a8] px-3 py-2.5 flex items-center gap-3 hover:bg-[#fef6da] transition-colors"
                >
                  <span className="shrink-0 flex items-center gap-0.5 text-sm font-extrabold text-[#171426] bg-white rounded-full px-2 py-1 border border-[#f5e2a8]">
                    <span>{rt.from_avatar?.emoji || "🙂"}</span><span className="tabular-nums">{rt.from_avatar?.tag || ""}</span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm leading-none tracking-tight" aria-label={`${rt.stars} stars`} data-testid={`rating-stars-${i}`}>
                        {"⭐".repeat(Math.max(0, Math.min(5, rt.stars)))}
                      </span>
                      <span className="text-[11px] font-bold text-[#a16207]">{rt.stars}/5</span>
                      <span className="text-[10px] font-semibold text-[#a7a3b8] ml-auto">{timeAgo(rt.created_at)}</span>
                    </div>
                    <div className="text-[11px] text-[#9a96ac] truncate mt-0.5">on “{(rt.manual_title || "your video").slice(0, 28)}{(rt.manual_title || "").length > 28 ? "…" : ""}”</div>
                  </div>
                  {rt.hero_image_id && (
                    <img src={imageUrl(rt.hero_image_id)} alt="" className="shrink-0 w-12 h-12 rounded-lg object-cover border border-[#f5e2a8]" data-testid={`rating-thumb-${i}`} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Amazon breakdown — toggled open from the Amazon clicks card */}
      {panel === "amazon" && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-white p-3 sms-fade-up" data-testid="amazon-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#171426]">
              <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#f0a868,#c2410c)" }}><ShoppingCart size={15} className="text-white" /></span>
              Amazon clicks <span className="text-xs font-bold text-[#c2410c]">· {fmt(t.clicks)} total · {t.ctr}% CTR</span>
            </div>
            <button data-testid="amazon-card-close" onClick={() => { setPanel(null); setAmazonExpand(null); }} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
          </div>

          {(stats.amazon?.videos || []).length === 0 ? (
            <div className="py-8 text-center text-[#9a96ac]">
              <ShoppingCart size={26} className="mx-auto mb-2 text-[#f2c9a8]" />
              <p className="font-semibold text-[#6b6880]">No Amazon links yet.</p>
              <p className="text-sm mt-1">Add Amazon links to a video (in its edit panel) and clicks will show up here.</p>
            </div>
          ) : (
            <>
              {/* Spreadsheet-style header */}
              <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 pb-1.5 text-[10px] font-extrabold uppercase tracking-wide text-[#a48057] border-b border-[#f3e2d4]">
                <span>Video</span>
                <span className="text-right w-14">Clicks</span>
                <span className="text-right w-12">CTR</span>
              </div>
              <div className="max-h-[360px] overflow-y-auto" data-testid="amazon-list">
                {stats.amazon.videos.map((v) => {
                  const open = amazonExpand === v.id;
                  return (
                    <div key={v.id} className="border-b border-[#f7ede3] last:border-0">
                      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center px-3 py-2 hover:bg-[#fdf5ef] transition-colors" data-testid={`amazon-row-${v.id}`}>
                        <button
                          onClick={() => v.items?.length ? setAmazonExpand(open ? null : v.id) : onOpen?.(v.id)}
                          className="flex items-center gap-1.5 min-w-0 text-left"
                          title={v.items?.length ? "Show which products were clicked" : v.title}
                        >
                          {v.items?.length > 0 && (
                            <ChevronRight size={14} className={`shrink-0 text-[#c2410c] transition-transform ${open ? "rotate-90" : ""}`} />
                          )}
                          <span className="font-bold text-sm text-[#171426] truncate">{v.title}</span>
                        </button>
                        <span className="text-right w-14 font-extrabold tabular-nums text-[#c2410c]">{fmt(v.clicks)}</span>
                        <span className="text-right w-12 font-semibold tabular-nums text-[#6b6880]">{v.ctr}%</span>
                      </div>
                      {open && v.items?.length > 0 && (
                        <div className="px-3 pb-2 pl-8 space-y-1" data-testid={`amazon-items-${v.id}`}>
                          {v.items.map((it, i) => (
                            <a
                              key={i}
                              href={it.url}
                              target="_blank"
                              rel="noreferrer"
                              className="grid grid-cols-[1fr_auto] gap-2 items-center rounded-lg bg-[#fdf5ef] px-2.5 py-1.5 hover:bg-[#fbe9db] transition-colors"
                              data-testid={`amazon-item-${v.id}-${i}`}
                            >
                              <span className="flex items-center gap-1.5 min-w-0 text-xs font-semibold text-[#8a5a33] truncate">
                                <ExternalLink size={11} className="shrink-0" /> {it.name}
                              </span>
                              <span className="text-right font-extrabold tabular-nums text-[#c2410c] text-xs">{fmt(it.clicks)}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Commission note + quick links */}
          <div className="mt-3 rounded-xl bg-[#fdf5ef] border border-[#f3ddca] px-3 py-2.5 text-[12px] text-[#8a5a33] leading-snug" data-testid="amazon-commission-note">
            <b>Clicks are your interest signal.</b> Actual commissions ($) are calculated and paid on Amazon's side — check your earnings anytime in the Associates dashboard.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <a
              href="https://affiliate-program.amazon.com/home/account/tag/manage"
              target="_blank"
              rel="noreferrer"
              data-testid="amazon-earnings-link"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#c2410c] hover:bg-[#a83809] rounded-full px-3.5 py-1.5 transition-colors"
            >
              <ExternalLink size={13} /> Amazon earnings report
            </a>
            <button
              data-testid="amazon-manage-id"
              onClick={() => ctx.openSettings?.()}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#c2410c] bg-[#fbeadd] hover:bg-[#f7ddc9] rounded-full px-3.5 py-1.5 transition-colors"
            >
              <Settings2 size={13} /> Manage Amazon ID
            </button>
          </div>
        </div>
      )}

      {hasData && best && best.views > 0 && (
        <button
          data-testid="best-performer"
          onClick={() => onOpen?.(best.id)}
          className="mt-3 w-full text-left rounded-2xl border-2 border-[#5d8a52] bg-[#f0f6ec] p-3 flex items-center gap-3 hover:bg-[#e7f0e0] transition-colors"
        >
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#e5ecdf] shrink-0">
            {best.hero_image_id && <img src={imageUrl(best.hero_image_id)} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[#4f7746]">
              <TrendingUp size={12} /> Top performer
            </div>
            <div className="font-bold text-[#171426] truncate">{best.title}</div>
            <div className="text-xs font-semibold text-[#6b6880] mt-0.5">
              {fmt(best.views)} views · {fmt(best.clicks)} Amazon clicks · {best.rating_avg ? `★ ${best.rating_avg}` : "no ratings yet"}
            </div>
          </div>
        </button>
      )}

      {/* Trends */}
      {trendsOpen && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-black p-3" data-testid="studio-trends">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-sm font-bold text-white">
              <TrendingUp size={15} className="text-[#5b8cff]" /> Trends
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[{ v: 1, l: "24h" }, { v: 3, l: "3d" }, { v: 7, l: "7d" }, { v: 30, l: "30d" }].map(({ v, l }) => (
                  <button key={v} data-testid={`trend-range-${v}`} onClick={() => setTrendRange(v)}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
                    style={trendRange === v ? { background: "#1e3aaa", color: "#fff" } : { background: "#1a1a24", color: "#8ea2ff" }}>
                    {l}
                  </button>
                ))}
              </div>
              <button data-testid="trends-close" onClick={() => onCloseTrends?.()} className="text-[#8a8798] hover:text-white transition-colors" title="Close Trends"><X size={18} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sms-tweed rounded-xl bg-[#0f0f16] border border-[#1e2340] p-2.5" data-testid="trend-views">
              <div className="flex items-center gap-1.5 text-xs font-bold text-[#c7cbe0]"><Eye size={12} className="text-[#5b8cff]" /> Views · {fmt(trendViewsSum)} in {trendLabel}</div>
              <Sparkline values={tViews} stroke="#4f7cff" fill="rgba(79,124,255,0.16)" />
            </div>
            <div className="sms-tweed rounded-xl bg-[#0f0f16] border border-[#3a1620] p-2.5" data-testid="trend-clicks">
              <div className="flex items-center gap-1.5 text-xs font-bold text-[#c7cbe0]"><ShoppingCart size={12} className="text-[#ef4444]" /> Amazon clicks · {fmt(trendClicksSum)} in {trendLabel}</div>
              <Sparkline values={tClicks} stroke="#ef4444" fill="rgba(239,68,68,0.16)" />
            </div>
          </div>
        </div>
      )}

      {/* Needs attention */}
      {(stats.flagged || []).length > 0 && (
        <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-[#fdf3f3] p-3" data-testid="needs-attention">
          <div className="flex items-center gap-1.5 text-sm font-bold text-[#b91c1c] mb-2">
            <Flag size={15} /> Needs attention
          </div>
          <div className="space-y-1.5">
            {stats.flagged.map((v) => (
              <button
                key={v.id}
                data-testid={`flagged-${v.id}`}
                onClick={() => onOpen?.(v.id)}
                className="w-full text-left flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-2 hover:bg-[#fbeaea] transition-colors"
              >
                <span className="text-sm font-semibold text-[#171426] truncate">{v.title}</span>
                <span className="text-[11px] font-bold text-[#b91c1c] shrink-0">
                  {v.hidden ? "Hidden · " : ""}{v.flags} flag{v.flags === 1 ? "" : "s"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <UnlockGateModal open={!!gate} onClose={() => setGate(null)} feature={gate?.feature} min={gate?.min} count={gate?.count} />

      <BuyCreditsModal open={buyOpen} onClose={() => setBuyOpen(false)} initialAmount={25} headline="Add funds so your renders never stop" />

    </div>
  );
}
